import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  // faShoppingCart,
  faBars,
  faTimes,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import logo from "../../Elements/Images/logo.png";
import { useAuth } from "../../Context/AuthContext";
import Button from "./Button";
import {
  dashboardUrls,
  downloadableMaterial,
  profileUrl,
  publicUrls,
} from "../../Api/Auth/AppUrls";

const Navbar = () => {
  const { userData, logout, categoryData } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const navigate = useNavigate();

  // Generate course subitems dynamically based on visible categories
  const courseSubItems = useMemo(() => {
    if (!categoryData) return [];

    const categoryMap = {
      Civil: { link: publicUrls.CivilCourses },
      Mechanical: { link: publicUrls.MechanicalCourses },
    };

    return categoryData
      .filter((category) => category.visibility)
      .map((category) => ({
        label: category.visibleTitle || category.title,
        link: categoryMap[category.title]?.link || "#",
      }));
  }, [categoryData]);
  const NavbarItems = [
    { label: "Home", link: publicUrls.Home },
    ...(courseSubItems.length > 0
      ? [
          {
            label: "Our Courses",
            subItems: courseSubItems,
          },
        ]
      : []),
    { label: "About Us", link: publicUrls.About },
    { label: "News Archive", link: publicUrls.NewsArchive },
    { label: "Blogs", link: publicUrls.BlogsArchive },
    { label: "Contact Us", link: publicUrls.Contact },
  ];
  const firstName = userData?.name ? userData.name.split(" ")[0] : "Guest";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <>
      <div className="flex flex-wrap justify-between items-center mb-5 px-6 pt-4">
        {/* Logo */}
        <Link to="/">
          <img
            src={logo}
            alt="Logo"
            className="h-8 max-w-[210px] object-contain transition-transform duration-400 hover:scale-[1.02]"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="lg:mt-0 mt-3 hidden sm:flex items-center gap-4">
          <nav>
            <ul className="flex flex-wrap gap-6 md:px-4 relative">
              {NavbarItems.map((item, index) => (
                <li
                  key={index}
                  className="relative group"
                  onMouseEnter={() => setDropdownOpen(index)}
                  onMouseLeave={() => setDropdownOpen(null)}
                >
                  <Link
                    to={item.link}
                    className="font-medium capitalize text-sm ml-2 relative mt-1 hover:text-primary after:bg-primary after:absolute after:h-0.5 after:w-0 after:bottom-0 after:left-0 hover:after:w-full after:transition-all after:duration-300"
                  >
                    {item.label}
                  </Link>

                  {/* Dropdown Menu */}
                  {item.subItems && (
                    <ul
                      className={`absolute left-0 mt-2 w-48 bg-white shadow-lg rounded-md border border-gray-200 z-10 transition-all duration-200 ${
                        dropdownOpen === index
                          ? "opacity-100 visible"
                          : "opacity-0 invisible"
                      }`}
                    >
                      {item.subItems.map((subItem, subIndex) => (
                        <li key={subIndex}>
                          <Link
                            to={subItem.link}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            {subItem.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </nav>
          {/* <FontAwesomeIcon icon={faShoppingCart} className="text-lg mr-3" /> */}
          {userData ? (
            <>
              <Link
                to={
                  userData?.roleId === 2
                    ? dashboardUrls.student
                    : userData?.roleId === 6
                    ? downloadableMaterial.adminFreeModelTest
                    : dashboardUrls.admin
                }
                className="font-medium capitalize text-sm relative hover:text-primary after:bg-primary after:absolute after:h-0.5 after:w-0 after:bottom-0 after:left-0 hover:after:w-full after:transition-all after:duration-300"
              >
                Dashboard
              </Link>
              <Link
                to={profileUrl}
                className="font-medium capitalize text-sm ml-2 relative hover:text-primary after:bg-primary after:absolute after:h-0.5 after:w-0 after:bottom-0 after:left-0 hover:after:w-full after:transition-all after:duration-300"
              >
                Hello,
                <br /> {firstName}
              </Link>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button type="primary">Login</Button>
              </Link>
              <Link to="/register">
                <Button type="outline">Register</Button>
              </Link>
            </>
          )}
          {userData && (
            <Button type="outline" onClick={handleLogout}>
              Logout
            </Button>
          )}
        </div>

        {/* Mobile Menu Icon */}
        <div className="sm:hidden">
          <FontAwesomeIcon
            icon={menuOpen ? faTimes : faBars}
            className="text-2xl cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          />
        </div>

        {/* Mobile Side Menu */}
        <div
          className={`fixed top-0 right-0 h-full w-64 bg-white shadow-lg transform ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          } transition-transform duration-300 ease-in-out sm:hidden flex flex-col p-6`}
        >
          <button className="self-end mb-4" onClick={() => setMenuOpen(false)}>
            <FontAwesomeIcon icon={faTimes} className="text-2xl" />
          </button>
          {/* Navigation Items */}
          <ul className="w-full">
            {NavbarItems.map((item, index) => (
              <li key={index} className="w-full">
                {item.subItems ? (
                  <>
                    {/* Main Item (with Dropdown Toggle) */}
                    <button
                      onClick={() =>
                        setDropdownOpen(dropdownOpen === index ? null : index)
                      }
                      className="text-lg font-medium py-3 w-full flex justify-between items-center border-b border-gray-200"
                    >
                      {item.label}
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`transition-transform ${
                          dropdownOpen === index ? "rotate-180" : "rotate-0"
                        }`}
                      />
                    </button>

                    {/* Dropdown Submenu */}
                    <ul
                      className={`overflow-hidden transition-[max-height] duration-300 ${
                        dropdownOpen === index ? "max-h-40" : "max-h-0"
                      }`}
                    >
                      {item.subItems.map((subItem, subIndex) => (
                        <li key={subIndex} className="pl-4">
                          <Link
                            to={subItem.link}
                            className="block py-2 text-gray-700 hover:text-primary"
                            onClick={() => setMenuOpen(false)}
                          >
                            {subItem.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <Link
                    to={item.link}
                    className="text-lg font-medium py-3 block border-b border-gray-200"
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
          {userData ? (
            <>
              <Link
                to={
                  userData?.roleId === 2
                    ? dashboardUrls.student
                    : dashboardUrls.admin
                }
                className="text-lg font-medium capitalize relative mt-1 hover:text-primary after:bg-primary after:absolute after:h-0.5 after:w-0 after:bottom-0 after:left-0 hover:after:w-full after:transition-all after:duration-300"
              >
                Dashboard
              </Link>
              <Link
                to={profileUrl}
                className="text-lg font-medium capitalize relative mt-1 hover:text-primary after:bg-primary after:absolute after:h-0.5 after:w-0 after:bottom-0 after:left-0 hover:after:w-full after:transition-all after:duration-300"
              >
                Hello,
                <br /> {firstName}
              </Link>
            </>
          ) : (
            <div className="mt-6">
              <Link to="/login">
                <Button type="primary" className="w-full">
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button type="outline" className="w-full mt-2">
                  Register
                </Button>
              </Link>
            </div>
          )}
          {userData && (
            <Button type="outline" onClick={handleLogout} className="mt-4">
              Logout
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

export default Navbar;
